/**
 * Utility function to check if a file is a HEIC/HEIF image
 * @param {string} fileName - The name of the file
 * @returns {boolean} - True if the file is HEIC/HEIF
 */
export const isHeicFile = (fileName) => {
  if (!fileName) return false;
  const extension = fileName.split('.').pop()?.toLowerCase();
  return extension === 'heic' || extension === 'heif';
};

/**
 * Convert HEIC/HEIF file to JPEG blob.
 *
 * @param {Blob} heicBlob - The HEIC blob to convert
 * @param {number} [quality=0.8] - JPEG quality (0.1–1.0)
 * @returns {Promise<Blob>} - The converted JPEG blob
 */
export const convertHeicToJpeg = async (heicBlob, quality = 0.8) => {
  try {
    const { default: heic2any } = await import('heic2any');

    const convertedBlob = await heic2any({
      blob: heicBlob,
      toType: 'image/jpeg',
      quality,
    });

    // heic2any returns an array for multi-image HEICs; take the first frame
    return Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
  } catch (error) {
    console.error('Error converting HEIC to JPEG:', error);
    throw new Error('Failed to convert HEIC image');
  }
};

/**
 * Module-level cache: original presigned URL → converted blob URL.
 *
 * Blob URLs created by URL.createObjectURL() remain valid for the lifetime
 * of the page, so caching them is safe. This prevents re-fetching and
 * re-converting the same file when thumbnails are virtualised and remount,
 * and also prevents parallel duplicate conversions for the same file.
 *
 * Key   — original HEIC presigned URL
 * Value — either a resolved blob URL string, or an in-flight Promise<string>
 *         (storing the promise deduplicates concurrent requests for the same file)
 */
const heicCache = new Map();

/**
 * Concurrency limiter for heic2any calls.
 *
 * heic2any is CPU/memory-intensive. Running more than a few in parallel
 * causes failures when many HEIC thumbnails scroll into view at once.
 * New requests wait in a FIFO queue until a slot frees up.
 */
const MAX_CONCURRENT_CONVERSIONS = 3;
let activeConversions = 0;
const conversionQueue = [];

function runWhenSlotAvailable(fn) {
  return new Promise((resolve, reject) => {
    const attempt = () => {
      activeConversions++;
      Promise.resolve()
        .then(() => fn())
        .then(resolve, reject)
        .finally(() => {
          activeConversions--;
          if (conversionQueue.length > 0) {
            conversionQueue.shift()();
          }
        });
    };

    if (activeConversions < MAX_CONCURRENT_CONVERSIONS) {
      attempt();
    } else {
      conversionQueue.push(attempt);
    }
  });
}

/**
 * Convert a HEIC URL to a displayable JPEG blob URL, with caching and
 * optional quality control.
 *
 * @param {string} heicUrl - The URL of the HEIC image (used as cache key)
 * @param {number} [quality=0.8] - JPEG quality (0.1–1.0)
 * @returns {Promise<string>} - The object URL of the converted JPEG
 */
export const convertHeicUrlToJpeg = async (heicUrl, quality = 0.8) => {
  // Include quality in the cache key so thumbnails (0.3) and the full
  // viewer (0.8) each get their own conversion and don't collide.
  const cacheKey = `${heicUrl}::q${quality}`;

  // Return already-resolved blob URL immediately
  const cached = heicCache.get(cacheKey);
  if (typeof cached === 'string') return cached;

  // Deduplicate: if a conversion is already in-flight for this key, await it
  if (cached instanceof Promise) return cached;

  const conversionPromise = runWhenSlotAvailable(async () => {
    const response = await fetch(heicUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch HEIC image: ${response.status}`);
    }
    const heicBlob = await response.blob();
    const jpegBlob = await convertHeicToJpeg(heicBlob, quality);
    const blobUrl = URL.createObjectURL(jpegBlob);
    // Replace the in-flight promise with the resolved string
    heicCache.set(cacheKey, blobUrl);
    return blobUrl;
  });

  // Store the promise so concurrent callers await the same conversion
  heicCache.set(cacheKey, conversionPromise);

  try {
    return await conversionPromise;
  } catch (error) {
    // Remove failed entry so a retry is possible
    heicCache.delete(cacheKey);
    console.error('Error converting HEIC URL to JPEG:', error);
    throw error;
  }
};

/**
 * Revoke all cached blob URLs and clear the cache.
 * Call this when the file manager unmounts to free object URL memory.
 */
export const clearHeicCache = () => {
  heicCache.forEach((value) => {
    if (typeof value === 'string' && value.startsWith('blob:')) {
      URL.revokeObjectURL(value);
    }
  });
  heicCache.clear();
  // Drain the queue so pending conversions from a closed modal don't run
  conversionQueue.length = 0;
};

/**
 * Get the appropriate image URL for display.
 * For HEIC/HEIF files, converts to JPEG using the cache.
 * Accepts a quality parameter so thumbnails can use a lower value than
 * the full viewer.
 *
 * @param {string} imageUrl - The original image URL
 * @param {string} fileName - The file name (used to detect HEIC)
 * @param {number} [quality=0.8] - JPEG quality (0.1–1.0); use ~0.4 for thumbnails
 * @returns {Promise<string>} - The displayable image URL
 */
export const getDisplayableImageUrl = async (imageUrl, fileName, quality = 0.8) => {
  if (isHeicFile(fileName)) {
    return convertHeicUrlToJpeg(imageUrl, quality);
  }
  return imageUrl;
};
